import { useQuery } from "@tanstack/react-query";
import {
  type ActiveOperation,
  chatGetSessionOptions,
  listActiveOperations,
  listActiveOperationsQueryKey,
  type SrcSchemasChatMessageResponse,
} from "@/api";
import { visibleMessages } from "./ChatMessages";
import type { PendingTurn } from "./ChatThread";
import { isContinuation } from "./PausedNotice";

/** Operations that write a chat's reply; their `entity_id` is the chat's session UUID. */
const REPLY_TYPES: ReadonlySet<string> = new Set(["chat", "agent"]);
/** Running or paused, or `failed`: listed once that way when the list finds a reply whose worker died (MHS-793). */
const REPLY_STATUSES: ReadonlySet<string> = new Set(["running", "paused", "failed"]);

/** Clock skew allowed between the server that started a reply and the database that dated its messages. */
const CLOCK_SKEW_MS = 2_000;
/** While a reply's user message isn't saved yet, its chat is read again after 1 s, then less often, up to 5 s apart. */
const MESSAGE_POLL_MS = 1_000;
const MESSAGE_POLL_MAX_MS = 5_000;

/** A chat's reply the backend is writing: its operation, and when it started (ISO). */
export interface ReplyOperation {
  opId: string;
  startedAt?: string;
}

/** The operation writing this chat's reply, if one is going or just failed (the backend runs one reply per chat). */
export function replyOperation(operations: readonly ActiveOperation[], sessionUuid: string): ReplyOperation | null {
  const reply = operations.find(
    (op) =>
      op.entity_id === sessionUuid && REPLY_TYPES.has(op.operation_type ?? "") && REPLY_STATUSES.has(op.status ?? ""),
  );
  return reply?.operation_id ? { opId: reply.operation_id, startedAt: reply.started_at ?? undefined } : null;
}

/** Milliseconds since the epoch, or NaN. Python's microseconds are cut to milliseconds, which every browser parses. */
function timeOf(iso: string | null | undefined): number {
  return iso ? Date.parse(iso.replace(/(\.\d{3})\d+/, "$1")) : Number.NaN;
}

type Message = SrcSchemasChatMessageResponse;

/** A tool step: the assistant's call (often without text, so never shown) or its result. */
const isToolStep = (m: Message) => m.role === "tool" || (m.role === "assistant" && (m.toolCalls?.length ?? 0) > 0);
/** A turn's last row: its answer, a stopped reply or an error. A running reply saves its own only when it ends. */
const isTurnEnd = (m: Message) => m.role === "assistant" && !isToolStep(m);

/**
 * Where a reply's own saved rows start (its user message, then its tool steps), in all the rows: a row index;
 * "awaiting" when nothing of it is saved yet; "ended" when a turn ended after it started (it has finished, or a newer
 * turn took over). Its rows come after the turn before it ended (whose answer can fall within the clock skew allowed),
 * and were saved since it started. Its user message is the latest such one: an earlier one belongs to a turn that
 * ended without saving its end (its worker died) just before. Without a start time, the last user message is taken
 * to be its own.
 */
function ownStart(messages: readonly Message[], startedAt: string | undefined): number | "awaiting" | "ended" {
  const started = timeOf(startedAt);
  if (Number.isNaN(started)) return messages.findLastIndex((m) => m.role === "user");
  const lastEnd = messages.findLastIndex(isTurnEnd);
  if (lastEnd >= 0 && timeOf(messages[lastEnd].createdAt) > started + CLOCK_SKEW_MS) return "ended";
  const since = (m: Message, i: number) => i > lastEnd && timeOf(m.createdAt) >= started - CLOCK_SKEW_MS;
  const asked = messages.findLastIndex((m, i) => m.role === "user" && since(m, i));
  // A continuation (or a Retry) saves no message of its own: its rows start at its first tool step
  const at = asked >= 0 ? asked : messages.findIndex((m, i) => isToolStep(m) && since(m, i));
  return at < 0 ? "awaiting" : at;
}

/** The reply started but nothing of it is saved yet: its user message comes first, a moment after it starts. */
export function awaitsItsMessage(messages: readonly Message[], op: ReplyOperation): boolean {
  return ownStart(messages, op.startedAt) === "awaiting";
}

/** How long to wait before reading the chat again, `waited` ms into waiting for a reply's message. */
export function messagePollDelay(waited: number): number {
  const doublings = Math.floor(Math.log2(Math.max(0, waited) / MESSAGE_POLL_MS + 1));
  return Math.min(MESSAGE_POLL_MS * 2 ** doublings, MESSAGE_POLL_MAX_MS);
}

/**
 * The turn a running reply belongs to. Its own rows are its user message, which the engine saves a moment after the
 * reply starts, then its tool steps, which stream again in full with the reply. Until its message is saved the reply
 * shows after the saved messages, without one (`prompt` unset). A continuation (or a Retry) answers a message saved
 * before it started, which shows as saved. Null when it has ended meanwhile, or a newer turn left it behind (its
 * worker died).
 */
export function reattachedTurn(
  messages: readonly Message[],
  sessionUuid: string,
  op: ReplyOperation,
): PendingTurn | null {
  const at = ownStart(messages, op.startedAt);
  if (at === "ended" || at === -1) return null;
  if (at === "awaiting") {
    return { sessionUuid, opId: op.opId, baseCount: visibleMessages(messages).length, startedAt: op.startedAt };
  }
  if (messages.slice(at + 1).some((m) => m.role === "user")) return null;
  const own = messages[at];
  const baseCount = visibleMessages(messages.slice(0, at)).length;
  return own.role === "user"
    ? {
        sessionUuid,
        opId: op.opId,
        prompt: own.content,
        baseCount,
        startedAt: own.createdAt,
        // What its saved message was: a Continue (shown as a chip) or a proposal's choice; Retry resends neither.
        continuation: isContinuation(own) || undefined,
        decision: own.messageMetadata?.confirmation ? true : undefined,
      }
    : { sessionUuid, opId: op.opId, baseCount, startedAt: op.startedAt };
}

/**
 * A reply that kept running while this page wasn't showing it (a reload, another tab, a chat left mid-reply), as
 * a turn to stream again from the start; or, for the turn `pending` already shows, the same turn under its user
 * message once that's saved. Null when there's nothing to change, or `ignore` says the page already has the reply.
 * Both reads are fresh for each chat opened: the page stays mounted from chat to chat, and cached data could name
 * a reply that has since finished, or predate its user message.
 */
export function useReattachTurn(
  sessionUuid: string | undefined,
  pending: PendingTurn | null,
  ignore: (opId: string) => boolean,
): PendingTurn | null {
  const active = useQuery({
    // Keyed per chat so that opening a chat checks again; listActiveOperationsQueryKey() still matches it.
    queryKey: [...listActiveOperationsQueryKey(), sessionUuid],
    queryFn: async ({ signal }) => (await listActiveOperations({ signal, throwOnError: true })).data,
    enabled: Boolean(sessionUuid),
    staleTime: 0,
    select: (r) => (sessionUuid ? replyOperation(r.data, sessionUuid) : null),
  });
  // isFetchedAfterMount restarts with each key, so it means "fetched since this chat opened".
  const found = active.isFetchedAfterMount ? active.data : null;
  const op = found && !ignore(found.opId) ? found : null;
  // The turn shown for a reply whose user message wasn't saved when it was picked up.
  const waiting = pending && pending.sessionUuid === sessionUuid && pending.prompt === undefined ? pending : null;
  const target: ReplyOperation | null = op ?? (waiting && { opId: waiting.opId, startedAt: waiting.startedAt });
  const waitStarted = active.dataUpdatedAt;
  const session = useQuery({
    ...chatGetSessionOptions({ path: { session_uuid: sessionUuid ?? "" } }),
    // Read again once a reply is found, so the saved messages include its user message.
    enabled: target !== null,
    staleTime: 0,
    select: (r) => r.data.messages ?? [],
    // Until it is (the reply starts first, its message is saved a moment later, or later with large attachments),
    // or the reply ends.
    refetchInterval: (query) =>
      target && !waiting?.settled && awaitsItsMessage(query.state.data?.data.messages ?? [], target)
        ? messagePollDelay(Date.now() - waitStarted)
        : false,
  });
  if (!sessionUuid || !session.data) return null;
  if (op) return session.isFetchedAfterMount ? reattachedTurn(session.data, sessionUuid, op) : null;
  if (!waiting) return null;
  const saved = reattachedTurn(session.data, sessionUuid, { opId: waiting.opId, startedAt: waiting.startedAt });
  return saved?.prompt === undefined
    ? null
    : {
        ...waiting,
        prompt: saved.prompt,
        baseCount: saved.baseCount,
        startedAt: saved.startedAt,
        continuation: saved.continuation,
        decision: saved.decision,
      };
}
